import React, { useState } from "react";
import {
  ExternalLink,
  ArrowUpRight,
  Rocket,
  Youtube,
  Instagram,
  Layers,
  Briefcase,
  ShieldCheck,
  Globe,
} from "lucide-react";

import focusin360logo from "../Images/360logo.png";
import talkytoonlogo from "../Images/TOKOlogo512.png";
import EvalLogo from "../Images/EvalNDT LOGO.png"

const VENTURES_DATA = [
  {
    id: 1,
    name: "JobDirectly",
    role: "Founder",
    type: "Industrial",
    status: "Active",
    description:
      "A comprehensive recruitment gateway bridging the gap between technical expertise and corporate recruiters across all industrial sectors. We provide direct access to the internal career portals of market leaders in Energy, Construction, Manufacturing, and Infrastructure and many more.",
    impact: "Cross-Sector Workforce Enablement",
    tagline: "The shortest path between top talent and industry leaders.",
    tags: ["Multi-Sector", "Workforce Tech", "Direct Hiring"],
    link: "https://jobdirectly.com",
    logo: "https://jobdirectly.com/static/media/JD_Logo.9d693e5593d7d89a9bc4.png", // Add import
    social: { linkedin: "https://linkedin.com/company/jobdirectly" },
  },
  {
    id: 2,
    name: "EvalNDT",
    role: "Founder",
    type: "Industrial",
    status: "Active",
    description:
      "A specialized digital reporting ecosystem that streamlines industrial inspections. It enables seamless file uploads and automated data processing to generate comprehensive, industry-standard reports instantly.",
    impact: "Paperless Inspection Workflows",
    tagline: "From raw data to professional reports in seconds.",
    tags: ["Digital Reporting", "Cloud Storage", "Process Automation"],
    link: "https://evalndt-4c0ea.web.app/",
    logo: EvalLogo, // Replace with your import
    social: {
      linkedin: "https://www.linkedin.com/company/evalndt",
    },
  },
  {
    id: 3, // Updated ID
    name: "FocusIn360",
    role: "Founder & CEO",
    type: "Industrial",
    status: "Scaling",
    description:
      "Connecting talent with opportunities: providing jobs in industrial technology and helping professionals find meaningful careers.",
    impact: "100+ Industrial Plants Secured",
    tagline: "Empowering professionals and industries globally.",
    tags: ["Jobs & Careers", "NDT Solutions", "Industry 4.0"],
    link: "https://www.focusin360.com/",
    logo: focusin360logo,
    social: {
      twitter: "",
    },
  },
  {
    id: 4, // Updated ID
    name: "TalkyToon",
    role: "Founder",
    type: "Creative",
    status: "Active",
    description:
      "An educational ecosystem leveraging music and rhythmic engagement to accelerate early childhood speech development.",
    impact: "50,000+ Young Learners Reached",
    tagline: "Where learning meets fun and creativity.",
    tags: ["EdTech", "Music Therapy", "Interactive Media"],
    link: "https://talkytoon.com",
    logo: talkytoonlogo,
    social: {
      youtube: "https://www.youtube.com/@TalkyToonOfficial",
      instagram: "https://www.instagram.com/talkytoonofficial",
    },
  },
];

const Ventures = () => {
  const [filter, setFilter] = useState("All");
  const categories = ["All", "Industrial", "Creative"];

  const filtered =
    filter === "All"
      ? VENTURES_DATA
      : VENTURES_DATA.filter((v) => v.type === filter);

  return (
<div className="bg-slate-50 min-h-screen pb-12 md:pb-20">
      {/* 1. HERO HEADER - Responsive adjustments for padding and font size */}
      <section className="bg-white border-b border-slate-200 pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 md:w-10 h-[1px] bg-cyan-600" />
                <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] md:tracking-[0.4em] uppercase font-black text-slate-400">
                  Strategic Portfolio
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-slate-950 tracking-tighter leading-tight">
                Active <span className="text-cyan-600 font-light">Ventures</span>
              </h1>
            </div>
            
            {/* Category Tabs - Mobile: Full width scroll / Tablet+: compact */}
            <nav className="flex flex-wrap gap-2 overflow-x-auto pb-2 lg:pb-0 lg:mb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`whitespace-nowrap px-4 md:px-6 py-2 rounded-lg text-[10px] md:text-xs font-bold uppercase tracking-wide transition-all ${
                    filter === cat
                      ? "bg-slate-950 text-white shadow-lg"
                      : "bg-slate-200/50 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* 2. VENTURES GRID - Responsive gap and margin adjustments */}
{/* 2. VENTURES GRID */}
<div className="max-w-7xl mx-auto px-4 md:px-6 mt-12 md:mt-16">
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
    {filtered.map((venture, idx) => (
      <div key={idx} className="space-y-6 md:space-y-8 group">
        
        {/* Venture Header with Logo Integration */}
        <div className="flex items-center gap-4">
          {/* Logo Container */}
          <div className="relative">
            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden p-2 shadow-sm group-hover:border-cyan-500/50 transition-all duration-500`}>
              <img 
                src={venture.logo} 
                alt={`${venture.name} logo`} 
                className="w-full h-full object-contain"
              />
            </div>
            {/* Status Indicator Dot */}
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-500 border-2 border-slate-50 rounded-full animate-pulse" />
          </div>

          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-slate-950 group-hover:text-cyan-600 transition-colors">
            {venture.name}
          </h2>
        </div>

        {/* Technical Profile Card */}
        <div className="bg-white border border-slate-200 rounded-[2rem] md:rounded-3xl p-6 md:p-8 shadow-sm group-hover:border-cyan-200 group-hover:shadow-md transition-all">
          <div className="flex justify-between items-start mb-6">
             <h3 className="font-mono text-[9px] md:text-[10px] font-black uppercase tracking-widest text-cyan-600">
              Venture Overview
            </h3>
            <span className="bg-slate-950 text-white px-3 py-1 rounded-lg font-mono text-[9px] md:text-[10px] font-black uppercase tracking-widest">
              {venture.status}
            </span>
          </div>

          <div className="mb-6">
            <p className="font-bold text-slate-400 text-[10px] md:text-xs uppercase mb-3 tracking-tighter">
              {venture.role}
            </p>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed">
              {venture.description}
            </p>
          </div>

          {/* Impact List */}
          <div className="space-y-3 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5" />
              <span className="text-slate-950 font-medium text-sm md:text-base">
                <span className="font-bold uppercase text-[10px] text-slate-400 mr-2">Key Impact:</span> 
                {venture.impact}
              </span>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-slate-50">
            <div className="flex gap-4">
              {venture.social?.youtube && (
                <a href={venture.social.youtube} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-red-600 p-1 transition-colors">
                  <Youtube size={20} />
                </a>
              )}
              {venture.social?.linkedin && (
                <a href={venture.social.linkedin} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-cyan-600 p-1 transition-colors">
                  <ExternalLink size={20} />
                </a>
              )}
            </div>
            
            <a 
              href={venture.link} 
              className="inline-flex items-center gap-2 text-slate-950 font-black text-[10px] md:text-xs uppercase tracking-[0.2em] group/link hover:text-cyan-600 transition-colors"
            >
              Access Venture <ArrowUpRight size={16} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>

        {/* Venture Tags */}
        <div className="flex flex-wrap gap-2">
          {venture.tags.map((tag, tIdx) => (
            <span key={tIdx} className="px-3 md:px-4 py-1.5 md:py-2 bg-slate-200/50 text-slate-700 rounded-lg text-[10px] md:text-xs font-bold uppercase tracking-wide">
              {tag}
            </span>
          ))}
        </div>
      </div>
    ))}
  </div>
</div>
    </div>
  );
};

export default Ventures;
